import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';

/**
 * TosGate - Modal overlay shown after login if the user has not accepted
 * the current Terms of Service. Blocks interaction until accepted.
 *
 * Usage: Place inside <AuthContext.Provider> in App.js.
 * It checks localStorage for `tos_accepted` flag (set during login) and
 * verifies with the backend. When the user accepts, it records consent
 * and dismisses itself.
 */
function TosGate() {
  const { user } = useContext(AuthContext);
  const [visible, setVisible] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !user.token) {
      setVisible(false);
      return;
    }

    // Check if ToS was already accepted (set by login response)
    const tosFlag = localStorage.getItem('tos_accepted');
    if (tosFlag === 'true') {
      setVisible(false);
      return;
    }

    // Verify with backend
    const checkConsent = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/consent-status`, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.tos_required) {
            setVisible(true);
          } else {
            localStorage.setItem('tos_accepted', 'true');
            setVisible(false);
          }
        }
      } catch (err) {
        // If check fails, don't block the user
        console.warn('ToS consent check failed:', err);
      }
    };

    checkConsent();
  }, [user]);

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/accept-terms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ consents: ['terms_of_service', 'privacy_policy'] })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to accept terms');
      }
      localStorage.setItem('tos_accepted', 'true');
      setVisible(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setAccepting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="modal-overlay" style={{ zIndex: 10000 }}>
      <div className="modal-card" style={{ maxWidth: '560px', padding: '2rem' }}>
        <h3 style={{ marginTop: 0 }}>Terms of Service</h3>
        <p className="status-text">
          Please review and accept our Terms of Service and Privacy Policy to continue using Rentual.
        </p>
        <div style={{
          background: 'var(--bg-secondary, #f9fafb)',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          maxHeight: '220px',
          overflowY: 'auto'
        }}>
          <p className="status-text" style={{ margin: '0 0 0.5rem' }}>
            <strong>Key points:</strong>
          </p>
          <ul className="status-text" style={{ margin: 0, paddingLeft: '1.25rem' }}>
            <li>Rentual provides tools for property management, tenant screening, and rent collection.</li>
            <li>Screening reports are sourced from third-party providers; Rentual does not generate credit data.</li>
            <li>Documents may be analyzed using AI services for risk assessment (advisory only).</li>
            <li>Landlords are responsible for all rental decisions and compliance with applicable laws.</li>
            <li>Your personal information is handled in accordance with PIPEDA.</li>
            <li>We never sell your personal information.</li>
          </ul>
        </div>
        <p className="status-text" style={{ marginBottom: '1rem' }}>
          Read the full{' '}
          <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
        </p>
        {error && <p className="error-message" style={{ marginBottom: '0.75rem' }}>{error}</p>}
        <button
          className="primary-button"
          onClick={handleAccept}
          disabled={accepting}
          style={{ width: '100%' }}
        >
          {accepting ? 'Accepting...' : 'I agree to the Terms of Service and Privacy Policy'}
        </button>
      </div>
    </div>
  );
}

export default TosGate;
